import { Button, ButtonProps, useToast } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { FaFileImport } from "react-icons/fa";
import axios from "../../utils/RequestProvider";

interface ImportCSVButtonProps extends Omit<ButtonProps, 'onClick'> {
    endpoint: string;
    label?: string;
    templateUrl?: string;
    accept?: string;
    onImported?: (res: any) => void;
}

export default function ImportCSVButton({
    endpoint,
    label = 'Import',
    templateUrl,
    accept = '.csv,.xlsx,.xls',
    onImported,
    ...rest
}: ImportCSVButtonProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const toast = useToast();

    const pickFile = () => inputRef.current?.click();

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const body = new FormData();
            body.append('file', file);
            const res = await axios.post(endpoint, body, {
                headers: { 'Content-Type': 'multipart/form-data' },
                validateStatus: (s) => s >= 200,
            });
            const data = res?.data;
            if (data?.status === 'error') {
                toast({
                    title: 'Import failed',
                    description: data.message || 'Could not import file.',
                    status: 'error',
                    duration: 2000,
                    isClosable: true,
                    position: 'top-right',
                });
            } else {
                toast({
                    title: 'Imported',
                    description: data?.message || `${file.name} uploaded.`,
                    status: 'success',
                    duration: 1500,
                    isClosable: true,
                    position: 'top-right',
                });
                onImported?.(data);
            }
        } catch (err) {
            toast({
                title: 'Import failed',
                status: 'error',
                duration: 2000,
                isClosable: true,
                position: 'top-right',
            });
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    return (
        <>
            <input
                ref={inputRef}
                type={'file'}
                accept={accept}
                style={{ display: 'none' }}
                onChange={handleChange}
            />
            <Button
                size={'sm'}
                variant={'outline'}
                leftIcon={<FaFileImport />}
                onClick={pickFile}
                isLoading={uploading}
                {...rest}
            >
                {label}
            </Button>
            {templateUrl && (
                <Button
                    as={'a'}
                    href={templateUrl}
                    download
                    size={'sm'}
                    variant={'ghost'}
                    ml={1}
                >
                    Template
                </Button>
            )}
        </>
    );
}
